import { useRef, useMemo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { DeleteIcon, EditIcon } from '../Icons';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePositionByPoint } from '@/hooks/usePositionByPoint';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { useClickOutside } from '@/hooks/useClickOutside';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import './ContextMenu.css';
import { ContextMenuDivider } from '@/components/ui/ContextMenu/ContextMenuDivider';

export interface ContextMenuItem {
  label: string;
  onClick: () => void;
  /** Если задан — вызывается на mousedown (удобно для копирования без сброса выделения текста). */
  onMouseDown?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  danger?: boolean;
  disabled?: boolean;
  /** Иконка слева от подписи (если не задана, для danger показывается DeleteIcon, для «Редактировать» — EditIcon). */
  icon?: ReactNode;
}

/** Запись-разделитель в массиве `items` */
export interface ContextMenuDividerEntry {
  readonly divider: true;
}

export type ContextMenuItemsEntry = ContextMenuItem | ContextMenuDividerEntry;

function isContextMenuDividerEntry(e: ContextMenuItemsEntry): e is ContextMenuDividerEntry {
  return 'divider' in e && e.divider === true;
}

interface ContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
  anchorPosition: { x: number; y: number } | null;
  /** Элемент-триггер: при наличии меню привязывается к нему через usePopoverPositioning */
  anchorEl?: HTMLElement | null;
  /** Вертикальное выравнивание: 'top' — от точки вниз; 'center' — центр меню по Y точки */
  alignY?: 'top' | 'center';
  /** Элементы, клик по которым не закрывает меню (например область таблицы при выделении) */
  excludeRefs?: React.RefObject<HTMLElement | null>[];
  /** Селектор: клики по элементу не закрывают меню (например кнопки меню для переключения) */
  excludeSelector?: string;
  items?: ContextMenuItemsEntry[];
  children?: ReactNode;
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Дополнительные классы на корневой `.context-menu` (порядок контента/анимаций). */
  menuClassName?: string;
  /** Поверх вложенных попапов (по умолчанию 1000). */
  zIndex?: number;
  /**
   * При привязке к anchorEl: ось приоритета размещения.
   * 'x' — сначала слева/справа (подменю), 'y' — сначала ниже/выше кнопки (выпадающее меню).
   */
  anchorPriorityAxis?: 'x' | 'y';
  /**
   * Следование за якорем при scroll (window, capture). Внутри программных скролл-контейнеров
   * возможны ложные срабатывания и дёргание меню — для якоря в виртуализированной таблице задайте `false`.
   */
  repositionOnScroll?: boolean;
  /**
   * Не вешать глобальный dismiss (mousedown / Escape): для «нижнего» слоя при двух одновременных меню.
   */
  disableClickOutside?: boolean;
  /** См. usePopoverPositioning — меньше rAF = быстрее первое размещение при смене якоря. */
  placementRafDepth?: number;
}

const CONTEXT_MENU_DEFAULT_Z = 1000;

export function ContextMenu({
  isOpen,
  onClose,
  anchorPosition,
  anchorEl,
  alignY = 'top',
  excludeRefs,
  excludeSelector,
  items = [],
  children,
  menuClassName,
  zIndex = CONTEXT_MENU_DEFAULT_Z,
  anchorPriorityAxis = 'x',
  repositionOnScroll = true,
  disableClickOutside = false,
  placementRafDepth,
}: ContextMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const lastPosRef = useRef(anchorPosition);
  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(isOpen, onClose, true);

  if (anchorPosition) lastPosRef.current = anchorPosition;
  const pos = anchorPosition ?? lastPosRef.current;
  const anchorPoint = useMemo(() => (pos ? { x: pos.x, y: pos.y } : null), [pos]);

  const useElement = !!anchorEl && shouldRender;

  usePopoverPositioning({
    anchorEl: useElement ? anchorEl : null,
    popoverRef: ref,
    isOpen: useElement,
    priorityAxis: anchorPriorityAxis,
    preferredPositionX: 'right',
    preferredPositionY: 'bottom',
    horizontalAlign: 'start',
    verticalAlign: alignY === 'center' ? 'center' : 'start',
    offset: 8,
    margin: { top: 20, bottom: 20, left: 20, right: 20 },
    zIndex,
    repositionOnScroll,
    ...(placementRafDepth != null ? { placementRafDepth } : {}),
  });

  usePositionByPoint({
    popoverRef: ref,
    isOpen: shouldRender && !!pos && !useElement,
    anchorPoint,
    offsetX: 0,
    offsetY: 8,
    alignY,
    zIndex,
  });

  useClickOutside(ref, requestClose, {
    enabled: shouldRender && !disableClickOutside,
    capture: false,
    excludeRefs: excludeRefs ?? [],
    excludeSelector: excludeSelector ? `.context-menu, ${excludeSelector}` : '.context-menu',
  });

  if (!shouldRender || (!pos && !anchorEl)) return null;

  const menuContent = (
    <div ref={ref} style={{ position: 'fixed', left: '-9999px', top: '-9999px' }}>
      <OverlayIntrinsicSize>
        <div className={clsx('context-menu', contentClasses, menuClassName)} role="menu">
          {children ??
            items.map((item, i) => {
              if (isContextMenuDividerEntry(item)) {
                return <ContextMenuDivider key={`divider-${i}`} />;
              }
              const resolvedIcon =
                item.icon !== undefined
                  ? item.icon
                  : item.danger
                    ? <DeleteIcon size={18} />
                    : item.label === 'Редактировать'
                      ? <EditIcon size={18} />
                      : null;
              return (
                <button
                  key={`item-${item.label}-${i}`}
                  type="button"
                  disabled={item.disabled}
                  className={`context-menu__item ${item.danger ? 'context-menu__item_danger' : ''} ${item.disabled ? 'context-menu__item_disabled' : ''}`}
                  onMouseDown={item.onMouseDown}
                  onClick={() => {
                    if (item.disabled) return;
                    item.onClick();
                    requestClose();
                  }}
                  role="menuitem"
                  aria-disabled={item.disabled ? true : undefined}
                >
                  <span className="context-menu__iconCell" aria-hidden>
                    {resolvedIcon}
                  </span>
                  <span className="context-menu__item-text">{item.label}</span>
                </button>
              );
            })}
        </div>
      </OverlayIntrinsicSize>
    </div>
  );

  return createPortal(menuContent, document.body);
}

export { ContextMenuDivider } from './ContextMenuDivider';
export type { ContextMenuDividerProps } from './ContextMenuDivider';
export { ContextMenuSectionHeader } from './ContextMenuSectionHeader';
export type { ContextMenuSectionHeaderProps } from './ContextMenuSectionHeader';
